import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Chip,
  Input,
  Textarea,
  Select,
  Option,
  Button,
  Spinner,
} from '@material-tailwind/react';
import {
  CurrencyDollarIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const formatPrice = (value, currency = 'USD') => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const getConfidenceColor = (confidence) => {
  if (!confidence) return 'gray';
  const lower = confidence.toLowerCase();
  if (lower.includes('high')) return 'green';
  if (lower.includes('medium')) return 'amber';
  if (lower.includes('low')) return 'red';
  return 'gray';
};

const CONFIDENCE_OPTIONS = [
  'High',
  'Medium-High',
  'Medium',
  'Medium-Low',
  'Low',
];

// Helper to extract values from content
const getContentValues = (content) => {
  if (!content) {
    return {
      estimatedValue: '',
      lowValue: '',
      highValue: '',
      confidence: '',
      appraisalText: '',
      methodology: '',
    };
  }
  return {
    estimatedValue: (content.estimatedValue || '').toString(),
    lowValue: (content.expectedRange?.low || '').toString(),
    highValue: (content.expectedRange?.high || '').toString(),
    confidence: content.confidenceLevel || '',
    appraisalText: content.appraisalText || '',
    methodology: content.methodology || '',
  };
};

function SummaryPricingSection({ content, onSave, onReset, isSaving }) {
  // Create a stable key from content to detect actual changes
  const contentKey = useMemo(() => {
    const vals = getContentValues(content);
    return JSON.stringify(vals);
  }, [content]);

  // Editable state
  const [editedEstimatedValue, setEditedEstimatedValue] = useState('');
  const [editedLowValue, setEditedLowValue] = useState('');
  const [editedHighValue, setEditedHighValue] = useState('');
  const [editedConfidence, setEditedConfidence] = useState('');
  const [editedAppraisalText, setEditedAppraisalText] = useState('');
  const [editedMethodology, setEditedMethodology] = useState('');

  // Reset form when content actually changes (using contentKey)
  useEffect(() => {
    const vals = getContentValues(content);
    setEditedEstimatedValue(vals.estimatedValue);
    setEditedLowValue(vals.lowValue);
    setEditedHighValue(vals.highValue);
    setEditedConfidence(vals.confidence);
    setEditedAppraisalText(vals.appraisalText);
    setEditedMethodology(vals.methodology);
  }, [contentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute hasChanges directly (no useEffect needed)
  const hasChanges = useMemo(() => {
    const orig = getContentValues(content);
    return (
      editedEstimatedValue !== orig.estimatedValue ||
      editedLowValue !== orig.lowValue ||
      editedHighValue !== orig.highValue ||
      editedConfidence !== orig.confidence ||
      editedAppraisalText !== orig.appraisalText ||
      editedMethodology !== orig.methodology
    );
  }, [
    editedEstimatedValue,
    editedLowValue,
    editedHighValue,
    editedConfidence,
    editedAppraisalText,
    editedMethodology,
    content,
  ]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave({
        estimatedValue: editedEstimatedValue ? parseInt(editedEstimatedValue, 10) : undefined,
        expectedRange: {
          low: editedLowValue ? parseInt(editedLowValue, 10) : undefined,
          high: editedHighValue ? parseInt(editedHighValue, 10) : undefined,
        },
        confidenceLevel: editedConfidence || undefined,
        appraisalText: editedAppraisalText || undefined,
        methodology: editedMethodology || undefined,
      });
    }
  }, [onSave, editedEstimatedValue, editedLowValue, editedHighValue, editedConfidence, editedAppraisalText, editedMethodology]);

  const handleReset = useCallback(() => {
    const vals = getContentValues(content);
    setEditedEstimatedValue(vals.estimatedValue);
    setEditedLowValue(vals.lowValue);
    setEditedHighValue(vals.highValue);
    setEditedConfidence(vals.confidence);
    setEditedAppraisalText(vals.appraisalText);
    setEditedMethodology(vals.methodology);
    if (onReset) {
      onReset();
    }
  }, [content, onReset]);

  if (!content) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>No pricing data available</Typography>
      </div>
    );
  }

  const currency = content.currency || 'USD';
  const dataPoints = content.dataPoints || content.data_points;
  const confidenceColor = getConfidenceColor(editedConfidence);

  return (
    <div className="space-y-3 p-2 overflow-auto h-full">
      {/* Hero Pricing Card - Display */}
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200">
        <CardBody className="p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <CurrencyDollarIcon className="h-5 w-5 text-blue-600" />
            <Typography variant="small" className="uppercase tracking-wide text-blue-600 font-semibold text-xs">
              Estimated Value
            </Typography>
            {hasChanges && (
              <Chip size="sm" value="Modified" color="amber" variant="ghost" className="ml-2" />
            )}
          </div>

          <Typography variant="h3" color="blue" className="mb-2">
            {formatPrice(editedEstimatedValue ? parseInt(editedEstimatedValue, 10) : null, currency)}
          </Typography>

          {(editedLowValue || editedHighValue) && (
            <Typography variant="small" color="gray" className="mb-2">
              Range: {formatPrice(editedLowValue ? parseInt(editedLowValue, 10) : null, currency)} — {formatPrice(editedHighValue ? parseInt(editedHighValue, 10) : null, currency)}
            </Typography>
          )}

          {editedConfidence && (
            <div className="flex justify-center">
              <Chip
                size="sm"
                value={editedConfidence}
                color={confidenceColor}
                icon={<ShieldCheckIcon className="h-4 w-4" />}
              />
            </div>
          )}

          {dataPoints !== undefined && (
            <Typography variant="small" color="gray" className="mt-2 text-xs">
              Based on {dataPoints} comparable data points
            </Typography>
          )}
        </CardBody>
      </Card>

      {/* Editable Pricing Fields */}
      <Card>
        <CardBody className="p-3 space-y-3">
          <Typography variant="small" className="font-medium text-xs text-blue-gray-700">
            Edit Pricing
          </Typography>

          <div className="grid grid-cols-3 gap-2">
            <Input
              type="number"
              label="Low Value"
              value={editedLowValue}
              onChange={(e) => setEditedLowValue(e.target.value)}
              size="md"
            />
            <Input
              type="number"
              label="Estimated Value"
              value={editedEstimatedValue}
              onChange={(e) => setEditedEstimatedValue(e.target.value)}
              size="md"
            />
            <Input
              type="number"
              label="High Value"
              value={editedHighValue}
              onChange={(e) => setEditedHighValue(e.target.value)}
              size="md"
            />
          </div>

          <Select
            label="Confidence Level"
            value={editedConfidence}
            onChange={(val) => setEditedConfidence(val)}
            size="md"
            className="border-t-blue-gray-200"
          >
            {CONFIDENCE_OPTIONS.map((opt) => (
              <Option key={opt} value={opt}>
                {opt}
              </Option>
            ))}
          </Select>
        </CardBody>
      </Card>

      {/* Appraisal Text - Editable */}
      <Card>
        <CardBody className="p-2">
          <div className="flex items-center gap-2 mb-2">
            <DocumentTextIcon className="h-4 w-4 text-gray-500" />
            <Typography variant="small" color="blue-gray" className="font-medium text-xs">
              Valuation Summary
            </Typography>
          </div>
          <Textarea
            value={editedAppraisalText}
            onChange={(e) => setEditedAppraisalText(e.target.value)}
            rows={4}
            className="text-xs"
            label="Appraisal text"
          />
        </CardBody>
      </Card>

      {/* Methodology - Editable */}
      <Card className="bg-gray-50">
        <CardBody className="p-2">
          <Typography variant="small" className="font-medium text-xs mb-2">
            Methodology
          </Typography>
          <Textarea
            value={editedMethodology}
            onChange={(e) => setEditedMethodology(e.target.value)}
            rows={3}
            className="text-xs"
            label="Methodology"
          />
        </CardBody>
      </Card>

      {/* Save/Reset Buttons */}
      {onSave && (
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outlined"
            size="sm"
            onClick={handleReset}
            disabled={!hasChanges || isSaving}
            className="py-1 px-3 text-xs"
          >
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="py-1 px-3 text-xs"
          >
            {isSaving ? (
              <>
                <Spinner className="h-3 w-3 mr-1" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default SummaryPricingSection;
